import { useEffect, useState } from "react";
import axios from 'axios';
import styled from "styled-components";
import fundo from "../../../../styles/fundo.svg";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { FaDownload } from "react-icons/fa";
import { FiZoomIn, FiZoomOut } from "react-icons/fi";


import logo1 from "./logo-1.png";
import logo6 from "./logo-6.png";


// ========== LOOK & FEEL MINIMALISTA ==========
const palette = {
  // fundo da página (se usar)
  cardBg: "#ffffff",
  headBg: "#0a7a3f",          // cabeçalho da tabela
  headText: "#ffffff",
  text: "#334155",
  textMuted: "#64748b",
  border: "#e5e7eb",
  zebra: "#f8fafc",

  accent: "#0ea5e9",
};

// Container do card/tabela
const Geral = styled.div`
  overflow: hidden;
  background: ${palette.cardBg};
  border: 1px solid ${palette.border};
  border-radius: 14px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
  margin: 10px auto;
  width: 100%;

  /* Envolve a tabela num scroller vertical suave */
  & > div {
    overflow: auto;
  }

  /* Scrollbar minimal */
  & > div::-webkit-scrollbar {
    height: 10px;
    width: 10px;
  }
  & > div::-webkit-scrollbar-track {
    background: transparent;
  }
  & > div::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 999px;
  }
  & > div::-webkit-scrollbar-thumb:hover {
    background: #c4c9d1;
  }

  @media (max-width: 1367px) {
    margin: 8px;
    box-shadow: 0 4px 14px rgba(15, 23, 42, 0.06);
  }

  @media (max-width: 1024px) {
    margin: 8px;
    margin-bottom: 100px;
  }
`;

// Tabela
const Table = styled.table`
  border-collapse: collapse;   /* 👈 junto com as bordas fica linear */
  width: 100%;
  font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto,
    "Helvetica Neue", Arial, "Noto Sans", "Liberation Sans", sans-serif;


  /* borda inferior de cada linha do corpo */
  tbody tr:not(:last-child) td {
    border-bottom: 1px solid ${palette.border};
  }
`;

// Cabeçalho sticky com fallback de cor
const Thead = styled.thead`
  position: sticky;
  top: 0;
  z-index: 5;
  background-color: ${palette.headBg};
  background-blend-mode: multiply;
  backdrop-filter: saturate(120%) blur(2px);
`;

// Linhas
export const Tr = styled.tr`
  /* ❌ remova esta linha:
  border-bottom: 1px solid ${palette.border};
  */

  ${({ noHover }) =>
    !noHover &&
    `
    &:hover {
      background-color: ${palette.zebra}; /* ou outra cor de hover */
    }
  `}

  @media (max-width: 1024px) {
    .hide-on-mobile {
      display: none;
    }
  }
`;

// Cabeçalho das colunas
export const Th = styled.th`
  color: ${palette.headText};
  padding: clamp(8px, 1.6vw, 14px) clamp(6px, 1.4vw, 10px);
  font-size: clamp(8px, 1.2vw, 11px);
  text-align: center;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  border-bottom: 1px solid rgba(255,255,255,0.08);
  white-space: nowrap;

  &.hide-on-mobile {
    @media (max-width: 1024px) {
      display: none;
    }
  }

  .tooltip {
    position: relative;
    cursor: help;
  }
  .tooltip .tooltiptext {
    visibility: hidden;
    width: max-content;
    background-color: #111827;
    color: #fff;
    text-align: left;
    border-radius: 8px;
    padding: 4px 8px;
    position: absolute;
    z-index: 10;
    bottom: 125%;
    left: 50%;
    transform: translateX(-50%);
    opacity: 0;
    transition: opacity .2s ease;
    white-space: nowrap;
    box-shadow: 0 8px 20px rgba(0,0,0,.2);
  }
  .tooltip:hover .tooltiptext {
    visibility: visible;
    opacity: 1;
  }
`;

// Células
export const Td = styled.td`
  padding: clamp(10px, 1.8vw, 16px) clamp(6px, 1.4vw, 10px);
  text-align: ${(props) => (props.alignCenter ? 'center' : 'left')};
  font-size: clamp(8px, 1.4vw, 11px);
  color: ${palette.text};
  background-color: ${(props) => (props.zebra ? palette.zebra : "transparent")};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  &.mobile-hide {
    @media (max-width: 1024px) {
      display: none;
    }
  }
`;

// Corpo
export const Tbody = styled.tbody`
  background-color: #fff;
`;

// TH que funciona como “cabeçalho-título” (linha 1)
export const CabecalhoTabela = styled.th`
  text-align: center;
  padding: clamp(10px, 2vw, 18px);
  color: ${palette.headText};
  background-color: ${palette.headBg};
  position: sticky;
  top: 0;
  z-index: 6; /* acima do Thead */
  border-bottom: 1px solid rgba(255,255,255,0.08);

  h2 {
    margin: 0;
    font-size: clamp(14px, 2.4vw, 18px);
    font-weight: 700;
    letter-spacing: 0.01em;
  }
`;

// Botão “fantasma” no header (usado para ícones)
const Button = styled.td`
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  background-color: transparent;
  color: ${palette.headText};
  opacity: .9;
  transition: transform .12s ease, opacity .12s ease;
  user-select: none;

  &:hover { transform: translateY(-1px); opacity: 1; }
  &:active { transform: translateY(0); opacity: .85; }

  @media (max-width: 780px) {
    font-size: 14px;
  }
`;

// Header do título/ações
const HeaderWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 12px;

  @media (max-width: 780px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;

    h2 { font-size: clamp(16px, 5vw, 20px); }
  }
`;

const HeaderRight = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;

  p {
    margin: 0;
    font-size: 12px;
    color: ${palette.headText};
    opacity: .85;
  }

  @media (max-width: 780px) {
    width: 100%;
    justify-content: space-between;
    p { font-size: 11px; }
  }
`;

const IconGroup = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;

  /* alvo o <td> Button que contém ícones */
  ${Button} {
    padding: 6px 8px;
    border-radius: 10px;
    background: rgba(255,255,255,0.06);
    border: 1px solid rgba(255,255,255,0.08);
  }
`;




const useBoletim = (tipo, urlApi, rota = "atualiza-cana-manual", periodo = null) => {
  const [dados, setDados] = useState([]);

  useEffect(() => {
    const fetchDados = async () => {
      try {
        const res = await axios.get(`${urlApi}boletins-externo/${rota}/${tipo}`);

        const ordem = ['d7', 'd6', 'd5', 'd4', 'd3', 'd2', 'd1', 'd', 'semana', 'mes', 'safra'];

        // 🔒 Verifica se é array antes de tentar ordenar
        if (Array.isArray(res.data)) {
          const dadosOrdenados = res.data.sort((a, b) => {
            return ordem.indexOf(a.PERIODO) - ordem.indexOf(b.PERIODO);
          });
          setDados(dadosOrdenados);
        } else {
          console.warn(`⚠️ Dados inesperados recebidos para tipo ${tipo}:`, res.data);
          setDados([]); // ou setDados([res.data]) se for um único objeto válido
        }

      } catch (error) {
        console.error(`Erro ao buscar ${tipo}:`, error);
      }
    };

    fetchDados();
  }, [tipo, urlApi, rota, periodo]);

  return dados;
};

const useHoraRodou = (urlApi, rota = "atualiza-cana-manual") => {
  const [hora, setHora] = useState(null);

  useEffect(() => {
    const fetchHora = async () => {
      try {
        const res = await axios.get(`${urlApi}boletins-externo/${rota}/hora-rodou`);
        setHora(res.data.hora); // já pega a string diretamente
      } catch (error) {
        console.error("Erro ao buscar hora-rodou:", error);
      }
    };

    fetchHora();
  }, [urlApi, rota]);

  return hora;
};

const Grid = ({ urlApi, periodos, zoomIn, zoomOut }) => {
  const horaRodou = useHoraRodou(urlApi);
  const talhoesEncerradosMetrosTeoricos = useBoletim("cm-talhoes-encerados-metros-teoricos", urlApi);
  const talhoesEncerradosMetrosTotais = useBoletim("cm-talhoes-encerados-metros-totais", urlApi);
  const talhoesEncerradosDifMetros = useBoletim("cm-talhoes_encerados_dif_metros", urlApi);
  const talhoesEncerradosVlrApontado = useBoletim("cm-talhoes_encerados_vlr_apontado", urlApi);
  const talhoesEncerradosCanaCortada = useBoletim("cm-talhoes_encerados_cana_cortada", urlApi);
  const talhoesEncerradosCustoMetro = useBoletim("cm-talhoes_encerados_custo_metro", urlApi);
  const talhoesEncerradosCustoTonelada = useBoletim("cm-talhoes_encerados_custo_tonelada", urlApi);

  const talhoesNaoEncerradosMetrosTeoricos = useBoletim("cm-talhoes_nao_encerados_metros_teoricos", urlApi);
  const talhoesNaoEncerradosMetrosTotais = useBoletim("cm-talhoes_nao_encerados_metros_totais", urlApi);
  const talhoesNaoEncerradosDifMetros = useBoletim("cm-talhoes_nao_encerados_dif_metros", urlApi);
  const talhoesNaoEncerradosVlrApontado = useBoletim("cm-talhoes_nao_encerados_vlr_apontado", urlApi);
  const talhoesNaoEncerradosCanaCortada = useBoletim("cm-talhoes_nao_encerados_cana_cortada", urlApi);
  const talhoesNaoEncerradosCustoMetro = useBoletim("cm-talhoes_nao_encerados_custo_metro", urlApi);
  const talhoesNaoEncerradosCustoTonelada = useBoletim("cm-talhoes_nao_encerados_custo_tonelada", urlApi);

  const totalTalhoesMetrosTeoricos = useBoletim("cm-total_talhoes_metros_teoricos", urlApi);
  const totalTalhoesMetrosTotais = useBoletim("cm-total_talhoes_metros_totais", urlApi);
  const totalTalhoesDifMetros = useBoletim("cm-total_talhoes_dif_metros", urlApi);
  const totalTalhoesVlrApontado = useBoletim("cm-total_talhoes_vlr_apontado", urlApi);
  const totalTalhoesCanaCortada = useBoletim("cm-total_talhoes_cana_cortada", urlApi);
  const totalTalhoesCustoMetro = useBoletim("cm-total_talhoes_custo_metro", urlApi);
  const totalTalhoesCustoTonelada = useBoletim("cm-total_talhoes_custo_tonelada", urlApi);





  //=======================JUNTA OS MAP======================================//
  const isMobile = window.innerWidth <= 768;
  const codigosPeriodos = ['d7', 'd6', 'd5', 'd4', 'd3', 'd2', 'd1', 'd', 'semana', 'mes', 'safra'];
  const periodosMobile = ['d1', 'd', 'semana', 'mes', 'safra'];

  const LinhaTabela = ({ titulo, dados2, decimais = 2 }) => {
    const periodosEmNegrito = ['d', 'semana', 'mes', 'safra'];

    const dadosFiltrados = isMobile
      ? dados2.filter(item => periodosMobile.includes(item.PERIODO))
      : dados2;

    return (
      <Tr>
        <Td><strong>{titulo}</strong></Td>
        {dadosFiltrados.map((item, index) => {
          const aplicarNegrito = periodosEmNegrito.includes(item.PERIODO);
          const valor = typeof item.VALOR === 'number'
            ? item.VALOR.toLocaleString("pt-BR", {
              minimumFractionDigits: decimais,
              maximumFractionDigits: decimais
            })
            : item.VALOR === 0 || item.VALOR === "0"
              ? "0"
              : "-";

          return (
            <Td
              key={`2-${index}`}
              alignCenter
              zebra={index % 2 === 0}
              style={{ width: "100px" }}
            >
              {aplicarNegrito ? <strong>{valor}</strong> : valor}
            </Td>
          );
        })}
      </Tr>
    );
  };






  //=======================GERAR PDF==========================================//

  const getBase64FromImage = (imgSrc) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "Anonymous";

      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        resolve({ base64: canvas.toDataURL("image/png"), width: img.naturalWidth, height: img.naturalHeight });
      };

      img.onerror = () => reject(new Error("Erro ao carregar imagem"));
      img.src = imgSrc;
    });
  };






  //===========================EXPORTAR PDF==============================//
  const gerarPdfRelatorio = async () => {
    const isMobile = window.innerWidth <= 768;
    const periodosOrdenados = ['d7', 'd6', 'd5', 'd4', 'd3', 'd2', 'd1', 'd', 'semana', 'mes', 'safra'];
    const periodosMobile = ['d7', 'd6', 'd5', 'd4', 'd3', 'd2', 'd1', 'd', 'semana', 'mes', 'safra'];
    const periodosFiltrados = isMobile ? periodosMobile : periodosOrdenados;

    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "A4" });

    // Marca d'água
    const { base64: marcaDaguaBase64, width: mw, height: mh } = await getBase64FromImage(logo1);
    const maxMarcaW = 400, maxMarcaH = 300;
    const ratioMarca = Math.min(maxMarcaW / mw, maxMarcaH / mh);
    const marcaW = mw * ratioMarca;
    const marcaH = mh * ratioMarca;
    const centerX = (842 - marcaW) / 2;
    const centerY = (595 - marcaH) / 2;

    // Cabeçalho com logo
    const { base64, width, height } = await getBase64FromImage(logo6);
    const ratio = Math.min(200 / width, 60 / height);
    doc.addImage(base64, 'PNG', 20, 10, width * ratio, height * ratio);
    doc.setFontSize(12);
    doc.setTextColor(87, 87, 87);
    doc.text("Boletim Cana Manual", 30, 70);
    doc.text(`Última atualização: ${horaRodou}`, 600, 70);

    // Mapeia os códigos d7, d6... para datas
    const mapaCodigosParaData = {};
    periodosOrdenados.forEach((codigo, index) => {
      if (periodos[index]) {
        mapaCodigosParaData[codigo] = periodos[index].DATA;
      }
    });
    mapaCodigosParaData['semana'] = 'Semana';
    mapaCodigosParaData['mes'] = 'Mês';
    mapaCodigosParaData['safra'] = 'Safra';

    const montarLinha = (titulo, dados) => {
      const linha = [titulo];
      periodosFiltrados.forEach(periodo => {
        const item = dados.find(d => d.PERIODO === periodo);
        linha.push(item && item.VALOR != 0 ? item.VALOR.toLocaleString("pt-BR", { minimumFractionDigits: 2 }) : "0");
      });
      return linha;
    };

    // Headers padrão
    const headers = [
      ["Período", ...periodosFiltrados.map(p => mapaCodigosParaData[p] || p.toUpperCase())]
    ];

    // === Bloco 1 ===
    doc.text("Talhões Encerrados", 14, 100);
    autoTable(doc, {
      startY: 110,
      head: headers,
      body: [
        montarLinha("Metros Teóricos", talhoesEncerradosMetrosTeoricos),
        montarLinha("Metros Totais", talhoesEncerradosMetrosTotais),
        montarLinha("Dif Metros", talhoesEncerradosDifMetros),
        montarLinha("Vlr Apontado", talhoesEncerradosVlrApontado),
        montarLinha("Cana Cortada", talhoesEncerradosCanaCortada),
        montarLinha("Custo Metro", talhoesEncerradosCustoMetro),
        montarLinha("Custo Tonelada", talhoesEncerradosCustoTonelada),
      ],
      margin: { left: 40, right: 20 },
      styles: {
        fontSize: 10,
        cellPadding: 4
      },
      headStyles: {
        fillColor: [7, 99, 51],
        textColor: 255
      },
    });

    // === Bloco 2 ===
    doc.text("Talhões Não Encerrados", 14, doc.lastAutoTable.finalY + 20);
    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 30,
      head: headers,
      body: [
        montarLinha("Metros Teóricos", talhoesNaoEncerradosMetrosTeoricos),
        montarLinha("Metros Totais", talhoesNaoEncerradosMetrosTotais),
        montarLinha("Dif Metros", talhoesNaoEncerradosDifMetros),
        montarLinha("Vlr Apontado", talhoesNaoEncerradosVlrApontado),
        montarLinha("Cana Cortada", talhoesNaoEncerradosCanaCortada),
        montarLinha("Custo Metro", talhoesNaoEncerradosCustoMetro),
        montarLinha("Custo Tonelada", talhoesNaoEncerradosCustoTonelada),
      ],
      margin: { left: 40, right: 20 },
      styles: {
        fontSize: 10,
        cellPadding: 4
      },
      headStyles: {
        fillColor: [7, 99, 51],
        textColor: 255
      },
    });

    // === Bloco 3 ===
    doc.text("Totais dos Talhões", 14, doc.lastAutoTable.finalY + 20);
    autoTable(doc, {
      startY: doc.lastAutoTable.finalY + 30,
      head: headers,
      body: [
        montarLinha("Metros Teóricos", totalTalhoesMetrosTeoricos),
        montarLinha("Metros Totais", totalTalhoesMetrosTotais),
        montarLinha("Dif Metros", totalTalhoesDifMetros),
        montarLinha("Vlr Apontado", totalTalhoesVlrApontado),
        montarLinha("Cana Cortada", totalTalhoesCanaCortada),
        montarLinha("Custo Metro", totalTalhoesCustoMetro),
        montarLinha("Custo Tonelada", totalTalhoesCustoTonelada),
      ],
      margin: { left: 40, right: 20 },
      styles: {
        fontSize: 10,
        cellPadding: 4
      },
      headStyles: {
        fillColor: [7, 99, 51],
        textColor: 255
      },
    });

    // Marca d’água no final (última página)
    doc.setGState(new doc.GState({ opacity: 0.08 }));
    doc.addImage(marcaDaguaBase64, 'JPEG', centerX, centerY, marcaW, marcaH);
    doc.setGState(new doc.GState({ opacity: 1 }));

    doc.save(`boletim_talhoes_${new Date().toLocaleString("pt-BR")}.pdf`);
  };




  //===========================TELA CHEIA==============================//
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch((err) => {
        console.log(`Error attempting to enable full - screen mode: ${err.message}(${err.name})`);
      });
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
  };






  return (
    <>

      <Geral>
        <div>

          <Table>
            <Thead>
              <Tr noHover>
                <CabecalhoTabela colSpan={13}>
                  <HeaderWrapper>
                    <h2 >Talhões Encerrados</h2>
                    <HeaderRight>
                      <p >Atualizado: {horaRodou}</p>
                      <IconGroup>
                        <Button onClick={gerarPdfRelatorio} placeholder="Imprimir">
                          <FaDownload />
                        </Button>
                        <Button
                          onClick={zoomOut}
                          title="Diminuir zoom (Alt+-)"
                          aria-label="Diminuir zoom"
                          type="button"
                        >
                          <FiZoomOut />
                        </Button>
                        <Button
                          onClick={zoomIn}
                          title="Aumentar zoom (Alt+=)"
                          aria-label="Aumentar zoom"
                          type="button"
                        >
                          <FiZoomIn />
                        </Button>
                      </IconGroup>
                    </HeaderRight>
                  </HeaderWrapper>
                </CabecalhoTabela>

              </Tr>


              <Tr noHover>

                <Th width="22%" >Período:</Th>


                {Array.isArray(periodos) &&
                  periodos
                    .map((item, index) => ({
                      ...item,
                      codigo: codigosPeriodos[index] // adiciona código associado à posição
                    }))
                    .filter(p => !isMobile || periodosMobile.includes(p.codigo))
                    .map((item, index) => (
                      <Th key={index}>
                        <div className="tooltip">
                          {item.DATA}
                          <span className="tooltiptext">{item.PERIODO}</span>
                        </div>
                      </Th>
                    ))
                }


              </Tr>
            </Thead>

            <Tbody>

              <LinhaTabela titulo="Metros Teóricos (mts)" dados2={talhoesEncerradosMetrosTeoricos} decimais={0} />
              <LinhaTabela titulo="Metros Totais (mts)" dados2={talhoesEncerradosMetrosTotais} decimais={0} />
              <LinhaTabela titulo="Diferença Metros (mts)" dados2={talhoesEncerradosDifMetros} decimais={2} />
              <LinhaTabela titulo="Valor Apontado" dados2={talhoesEncerradosVlrApontado} decimais={1} />
              <LinhaTabela titulo="Cana Cortada " dados2={talhoesEncerradosCanaCortada} decimais={2} />
              <LinhaTabela titulo="Custo Metro (real/mts)" dados2={talhoesEncerradosCustoMetro} decimais={3} />
              <LinhaTabela titulo="Custo Tonelada (real/ton)" dados2={talhoesEncerradosCustoTonelada} decimais={3} />


            </Tbody>
          </Table>

          <Table>
            <Thead>
              <Tr noHover>
                <CabecalhoTabela colSpan={13}>
                  <HeaderWrapper>
                    <h2 >Talhões Não Encerrados</h2>
                    <HeaderRight>
                      <p >Atualizado: {horaRodou}</p>
                    </HeaderRight>
                  </HeaderWrapper>
                </CabecalhoTabela>

              </Tr>


              <Tr noHover>

                <Th width="22%" >Período:</Th>


                {Array.isArray(periodos) &&
                  periodos
                    .map((item, index) => ({
                      ...item,
                      codigo: codigosPeriodos[index] // adiciona código associado à posição
                    }))
                    .filter(p => !isMobile || periodosMobile.includes(p.codigo))
                    .map((item, index) => (
                      <Th key={index}>
                        <div className="tooltip">
                          {item.DATA}
                          <span className="tooltiptext">{item.PERIODO}</span>
                        </div>
                      </Th>
                    ))
                }


              </Tr>
            </Thead>

            <Tbody>

              <LinhaTabela titulo="Metros Teóricos (mts)" dados2={talhoesNaoEncerradosMetrosTeoricos} decimais={0} />
              <LinhaTabela titulo="Metros Totais (mts)" dados2={talhoesNaoEncerradosMetrosTotais} decimais={0} />
              <LinhaTabela titulo="Diferença Metros (mts)" dados2={talhoesNaoEncerradosDifMetros} decimais={2} />
              <LinhaTabela titulo="Valor Apontado" dados2={talhoesNaoEncerradosVlrApontado} decimais={1} />
              <LinhaTabela titulo="Cana Cortada" dados2={talhoesNaoEncerradosCanaCortada} decimais={2} />
              <LinhaTabela titulo="Custo Metro (real/mts)" dados2={talhoesNaoEncerradosCustoMetro} decimais={3} />
              <LinhaTabela titulo="Custo Tonelada (real/ton)" dados2={talhoesNaoEncerradosCustoTonelada} decimais={3} />


            </Tbody>
          </Table>

          <Table>
            <Thead>
              <Tr noHover>
                <CabecalhoTabela colSpan={13}>
                  <HeaderWrapper>
                    <h2 >Total de Talhões</h2>
                    <HeaderRight>
                      <p >Atualizado: {horaRodou}</p>
                    </HeaderRight>
                  </HeaderWrapper>
                </CabecalhoTabela>

              </Tr>


              <Tr noHover>

                <Th width="22%" >Período:</Th>


                {Array.isArray(periodos) &&
                  periodos
                    .map((item, index) => ({
                      ...item,
                      codigo: codigosPeriodos[index] // adiciona código associado à posição
                    }))
                    .filter(p => !isMobile || periodosMobile.includes(p.codigo))
                    .map((item, index) => (
                      <Th key={index}>
                        <div className="tooltip">
                          {item.DATA}
                          <span className="tooltiptext">{item.PERIODO}</span>
                        </div>
                      </Th>
                    ))
                }


              </Tr>
            </Thead>

            <Tbody>

              <LinhaTabela titulo="Metros Teóricos (mts)" dados2={totalTalhoesMetrosTeoricos} decimais={0} />
              <LinhaTabela titulo="Metros Totais (mts)" dados2={totalTalhoesMetrosTotais} decimais={0} />
              <LinhaTabela titulo="Diferença Metros (mts)" dados2={totalTalhoesDifMetros} decimais={2} />
              <LinhaTabela titulo="Valor Apontado" dados2={totalTalhoesVlrApontado} decimais={1} />
              <LinhaTabela titulo="Cana Cortada" dados2={totalTalhoesCanaCortada} decimais={2} />
              <LinhaTabela titulo="Custo Metro (real/mts)" dados2={totalTalhoesCustoMetro} decimais={3} />
              <LinhaTabela titulo="Custo Tonelada (real/ton)" dados2={totalTalhoesCustoTonelada} decimais={3} />


            </Tbody>
          </Table>


        </div>

      </Geral >

    </>

  );
};



export default Grid;
